import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import {
    Button,
    Chip,
    Dropdown,
    Label,
    ListBox,
    Pagination,
} from '@heroui/react';
import { DataGrid, InlineSelect, Widget } from '@heroui-pro/react';
import type { DataGridColumn } from '@heroui-pro/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import {
    NO_IMAGE_ICON,
    SUPPLIER_COLUMN,
    SUPPLIERS_PAGE_SIZE_OPTIONS,
    formatBalance,
    formatUnallocated,
    getPaginationWindow,
    supplierRowActionsFor,
    type Supplier,
    type SupplierRowAction,
} from '../suppliersMeta';

type SupplierTableProps = {
    data: Supplier[];
    onDeleteSupplier: (id: string) => void;
    onEditSupplier: (id: string) => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    onRecordPayment: (id: string) => void;
    onSetArchived: (id: string, archived: boolean) => void;
    onViewSupplier: (id: string) => void;
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
};

const BALANCE_VARIANT_CLASS = {
    neutral: 'text-muted',
    success: 'text-success-soft-foreground',
    danger: 'text-danger-soft-foreground',
} as const;

/**
 * Render-only suppliers grid. The data is **server-paginated** — `data` is
 * already the current page in the server's order (by name), so there's no
 * client-side sort or slice here; the footer just drives the page / page-size
 * controls. Columns aren't sortable because the API has no sort parameter.
 */
export const SupplierTable: React.FC<SupplierTableProps> = ({
    data,
    onDeleteSupplier,
    onEditSupplier,
    onPageChange,
    onPageSizeChange,
    onRecordPayment,
    onSetArchived,
    onViewSupplier,
    page,
    pageSize,
    totalCount,
    totalPages,
}) => {
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            tableAria: t('suppliers.components.supplierTable.tableAria'),
            actionsAria: t('suppliers.components.supplierTable.actionsAria'),
            previous: t(
                'suppliers.components.supplierTable.pagination.previous',
            ),
            next: t('suppliers.components.supplierTable.pagination.next'),
            perPage: t('suppliers.components.supplierTable.pagination.perPage'),
            columns: {
                image: t('suppliers.components.supplierTable.columns.image'),
                name: t('suppliers.components.supplierTable.columns.name'),
                serialNumber: t(
                    'suppliers.components.supplierTable.columns.serialNumber',
                ),
                phone: t('suppliers.components.supplierTable.columns.phone'),
                email: t('suppliers.components.supplierTable.columns.email'),
                balance: t(
                    'suppliers.components.supplierTable.columns.balance',
                ),
            },
            empty: {
                title: t('suppliers.components.supplierTable.empty.title'),
            },
            fullyPaid: t(
                'suppliers.components.supplierTable.balance.fullyPaid',
            ),
            unallocated: t(
                'suppliers.components.supplierTable.balance.unallocated',
            ),
            archived: t('suppliers.components.supplierTable.archived'),
        }),
        [t],
    );

    const onRowAction = React.useCallback(
        (id: string, action: SupplierRowAction) => {
            switch (action) {
                case 'view':
                    onViewSupplier(id);
                    return;
                case 'payment':
                    onRecordPayment(id);
                    return;
                case 'edit':
                    onEditSupplier(id);
                    return;
                case 'archive':
                    onSetArchived(id, true);
                    return;
                case 'unarchive':
                    onSetArchived(id, false);
                    return;
                default:
                    onDeleteSupplier(id);
            }
        },
        [
            onDeleteSupplier,
            onEditSupplier,
            onRecordPayment,
            onSetArchived,
            onViewSupplier,
        ],
    );

    const columns = React.useMemo<DataGridColumn<Supplier>[]>(
        () => [
            {
                id: SUPPLIER_COLUMN.image,
                header: copy.columns.image,
                width: 72,
                cell: (item) =>
                    item.imageUrl ? (
                        <img
                            alt={item.name}
                            className="size-10 rounded-[8px] object-cover"
                            src={item.imageUrl}
                            {...getNodeTestIdProp(
                                `supplier-row-image-${item.id}`,
                            )}
                        />
                    ) : (
                        <div
                            aria-hidden
                            className="flex size-10 items-center justify-center rounded-[8px] bg-surface-secondary text-muted"
                            {...getNodeTestIdProp(
                                `supplier-row-image-fallback-${item.id}`,
                            )}
                        >
                            <Icon className="size-5" icon={NO_IMAGE_ICON} />
                        </div>
                    ),
            },
            {
                id: SUPPLIER_COLUMN.name,
                header: copy.columns.name,
                accessorKey: 'name',
                isRowHeader: true,
                cell: (item) => (
                    <span className="flex items-center gap-2">
                        <span
                            className="text-sm font-medium text-foreground"
                            {...getNodeTestIdProp(
                                `supplier-row-name-${item.id}`,
                            )}
                        >
                            {item.name}
                        </span>
                        {item.archived ? (
                            <Chip
                                color="default"
                                size="sm"
                                {...getNodeTestIdProp(
                                    `supplier-row-archived-${item.id}`,
                                )}
                            >
                                {copy.archived}
                            </Chip>
                        ) : null}
                    </span>
                ),
            },
            {
                id: SUPPLIER_COLUMN.serialNumber,
                header: copy.columns.serialNumber,
                accessorKey: 'serialNumber',
                width: 140,
                cell: (item) => (
                    <span
                        className="font-mono text-xs text-muted"
                        {...getNodeTestIdProp(
                            `supplier-row-serial-number-${item.id}`,
                        )}
                    >
                        {item.serialNumber ?? '—'}
                    </span>
                ),
            },
            {
                id: SUPPLIER_COLUMN.phone,
                header: copy.columns.phone,
                accessorKey: 'phone',
                width: 160,
                cell: (item) => (
                    <span
                        className="text-sm text-foreground"
                        {...getNodeTestIdProp(`supplier-row-phone-${item.id}`)}
                    >
                        {item.phone ?? '—'}
                    </span>
                ),
            },
            {
                id: SUPPLIER_COLUMN.email,
                header: copy.columns.email,
                accessorKey: 'email',
                cell: (item) => (
                    <span
                        className="text-sm text-foreground"
                        {...getNodeTestIdProp(`supplier-row-email-${item.id}`)}
                    >
                        {item.email ?? '—'}
                    </span>
                ),
            },
            {
                id: SUPPLIER_COLUMN.balance,
                header: copy.columns.balance,
                accessorKey: 'balance',
                width: 160,
                align: 'end',
                cell: (item) => {
                    const display = formatBalance(
                        item.balanceTetri,
                        copy.fullyPaid,
                    );
                    const unallocated = formatUnallocated(
                        item.unallocatedTotalTetri,
                        copy.unallocated,
                    );
                    return (
                        <div className="flex flex-col items-end gap-0.5">
                            <span
                                className={`text-sm font-medium tabular-nums ${BALANCE_VARIANT_CLASS[display.variant]}`}
                                {...getNodeTestIdProp(
                                    `supplier-row-balance-${item.id}`,
                                )}
                                data-balance-variant={display.variant}
                            >
                                {display.text}
                            </span>
                            {unallocated ? (
                                <span
                                    className="text-[11px] tabular-nums text-muted"
                                    {...getNodeTestIdProp(
                                        `supplier-row-unallocated-${item.id}`,
                                    )}
                                >
                                    {unallocated}
                                </span>
                            ) : null}
                        </div>
                    );
                },
            },
            {
                id: SUPPLIER_COLUMN.actions,
                header: '',
                pinned: 'end',
                width: 40,
                align: 'center',
                cellClassName: 'bg-surface',
                headerClassName: 'bg-surface-secondary',
                cell: (item) => (
                    <div
                        className="flex justify-center"
                        {...getNodeTestIdProp(
                            `supplier-row-actions-${item.id}`,
                        )}
                    >
                        <Dropdown>
                            <Button
                                aria-label={copy.actionsAria}
                                isIconOnly
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp(
                                    `supplier-row-actions-trigger-${item.id}`,
                                )}
                            >
                                <Icon icon="gravity-ui:ellipsis-vertical" />
                            </Button>
                            <Dropdown.Popover className="min-w-[160px]">
                                <Dropdown.Menu
                                    onAction={(key) =>
                                        onRowAction(
                                            item.id,
                                            key as SupplierRowAction,
                                        )
                                    }
                                >
                                    {supplierRowActionsFor(item.archived).map(
                                        (action) => (
                                            <Dropdown.Item
                                                id={action.id}
                                                key={action.id}
                                                textValue={t(action.labelKey)}
                                                variant={action.variant}
                                                {...getNodeTestIdProp(
                                                    `supplier-row-action-${action.id}-${item.id}`,
                                                )}
                                            >
                                                <Icon icon={action.icon} />
                                                <Label>
                                                    {t(action.labelKey)}
                                                </Label>
                                            </Dropdown.Item>
                                        ),
                                    )}
                                </Dropdown.Menu>
                            </Dropdown.Popover>
                        </Dropdown>
                    </div>
                ),
            },
        ],
        [copy, onRowAction, t],
    );

    const pageWindow = getPaginationWindow(totalPages, page);

    return (
        <Widget
            className="flex flex-col gap-0 overflow-hidden p-0"
            {...getNodeTestIdProp('supplier-table-widget')}
        >
            <DataGrid<Supplier>
                aria-label={copy.tableAria}
                columns={columns}
                data={data}
                getRowId={(item) => item.id}
                renderEmptyState={() => (
                    <div
                        className="flex flex-col items-center gap-3 py-12 text-muted"
                        {...getNodeTestIdProp('supplier-table-empty')}
                    >
                        <Icon
                            aria-hidden
                            className="size-8 text-default-500"
                            icon="gravity-ui:folder"
                        />
                        <span
                            className="text-sm"
                            {...getNodeTestIdProp('supplier-table-empty-title')}
                        >
                            {copy.empty.title}
                        </span>
                    </div>
                )}
                variant="primary"
                {...getNodeTestIdProp('supplier-table-grid')}
            />
            {totalCount > SUPPLIERS_PAGE_SIZE_OPTIONS[0]! ? (
                <div
                    className="flex flex-wrap items-center justify-between gap-3 border-t border-separator p-3"
                    {...getNodeTestIdProp('supplier-table-pagination')}
                >
                    <div className="flex items-center gap-2 text-xs text-muted">
                        <span>{copy.perPage}</span>
                        <InlineSelect
                            aria-label={copy.perPage}
                            onChange={(value) =>
                                value && onPageSizeChange(Number(value))
                            }
                            value={String(pageSize)}
                            {...getNodeTestIdProp('supplier-table-per-page')}
                        >
                            <InlineSelect.Trigger>
                                <InlineSelect.Value />
                                <InlineSelect.Indicator />
                            </InlineSelect.Trigger>
                            <InlineSelect.Popover className="w-[96px]">
                                <ListBox>
                                    {SUPPLIERS_PAGE_SIZE_OPTIONS.map((size) => (
                                        <ListBox.Item
                                            id={String(size)}
                                            key={size}
                                            textValue={String(size)}
                                        >
                                            {size}
                                            <ListBox.ItemIndicator />
                                        </ListBox.Item>
                                    ))}
                                </ListBox>
                            </InlineSelect.Popover>
                        </InlineSelect>
                    </div>
                    {totalPages > 1 ? (
                        <Pagination size="sm">
                            <Pagination.Content>
                                <Pagination.Item>
                                    <Pagination.Previous
                                        aria-label={copy.previous}
                                        isDisabled={page === 1}
                                        onPress={() => onPageChange(page - 1)}
                                    >
                                        <Pagination.PreviousIcon />
                                    </Pagination.Previous>
                                </Pagination.Item>
                                {pageWindow.map((entry, index) =>
                                    entry === 'ellipsis' ? (
                                        <Pagination.Item
                                            key={`supplier-table-page-ellipsis-${index}`}
                                        >
                                            <Pagination.Ellipsis />
                                        </Pagination.Item>
                                    ) : (
                                        <Pagination.Item key={entry}>
                                            <Pagination.Link
                                                isActive={entry === page}
                                                onPress={() =>
                                                    onPageChange(entry)
                                                }
                                                {...getNodeTestIdProp(
                                                    `supplier-table-page-${entry}`,
                                                )}
                                            >
                                                {entry}
                                            </Pagination.Link>
                                        </Pagination.Item>
                                    ),
                                )}
                                <Pagination.Item>
                                    <Pagination.Next
                                        aria-label={copy.next}
                                        isDisabled={page === totalPages}
                                        onPress={() => onPageChange(page + 1)}
                                    >
                                        <Pagination.NextIcon />
                                    </Pagination.Next>
                                </Pagination.Item>
                            </Pagination.Content>
                        </Pagination>
                    ) : null}
                </div>
            ) : null}
        </Widget>
    );
};
